/**
 * CR403/CR404: Category List Panel - left side of split view
 * CR404: Added icon rendering with priority (SVG > URL > placeholder)
 */

import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, Plus, FolderOpen } from "lucide-react";
import type { MerchantCategoryRow } from "@/hooks/useMerchantCategories";
import { cn } from "@/lib/utils";

interface CategoryListProps {
  categories: MerchantCategoryRow[];
  selectedId: string | null;
  onSelect: (category: MerchantCategoryRow) => void;
  onAddNew: () => void;
  isLoading?: boolean;
}

/**
 * Get icon to render based on priority: SVG > URL > placeholder
 */
function getCategoryIcon(category: MerchantCategoryRow): React.ReactNode {
  if (category.icon_svg) {
    return (
      <div
        className="w-4 h-4"
        dangerouslySetInnerHTML={{ __html: category.icon_svg }}
      />
    );
  }
  if (category.icon_url) {
    return (
      <img
        src={category.icon_url}
        alt={category.name}
        className="w-4 h-4 object-contain"
      />
    );
  }
  return <FolderOpen className="h-4 w-4 text-muted-foreground" />;
}

export function CategoryList({
  categories,
  selectedId,
  onSelect,
  onAddNew,
  isLoading,
}: CategoryListProps) {
  const [search, setSearch] = useState("");

  const filtered = categories.filter((cat) =>
    cat.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full border-r">
      {/* Header */}
      <div className="p-4 border-b space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Categories</h3>
          <Button size="sm" onClick={onAddNew}>
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search categories..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {/* List */}
      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="p-4 text-sm text-muted-foreground">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">
            {search ? "No categories match your search" : "No categories found"}
          </div>
        ) : (
          <div className="divide-y">
            {filtered.map((cat) => (
              <button
                key={cat.id}
                onClick={() => onSelect(cat)}
                className={cn(
                  "w-full text-left p-3 hover:bg-muted/50 transition-colors",
                  selectedId === cat.id && "bg-muted"
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded bg-muted flex items-center justify-center">
                    {getCategoryIcon(cat)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{cat.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {cat.merchants_count} merchants • {cat.sources_effective_count} sources
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
